import { useState } from 'react';
import { useForm } from 'react-hook-form';

import Grid from '@material-ui/core/Grid';
import Modal from 'components/CncUI/Modal';
import Table from 'components/CncUI/Table';
import Button from 'components/CncUI/Button';
import TextField from 'components/CncUI/TextField';

import EnumHelper from 'utilities/enumHelper';

const UpdateNamesModal = props => {
  const {
    form,
    setForm,
    accountsToUpdate,
    showDialog,
    setShowDialog,
    setConfirm,
    mappings,
  } = props;
  const [saving, setSaving] = useState(false);

  const accounts = accountsToUpdate
    .map(
      updatableAccount =>
        `${updatableAccount.routingNumber}-${updatableAccount.bankAccountNumber}`,
    )
    // Remove accounts removed post-adding names once
    .filter(temporaryId =>
      form.find(
        account =>
          `${account.routingNumber}-${account.bankAccountNumber}` ===
          temporaryId,
      ),
    )
    // Get current data from the form object coming from the parent
    .map(temporaryId =>
      form.find(
        account =>
          `${account.routingNumber}-${account.bankAccountNumber}` ===
          temporaryId,
      ),
    );
  const defaultValues = accounts.reduce((result, a) => {
    result[`bankName${a.routingNumber}-${a.bankAccountNumber}`] = (
      a.bankName || ''
    ).trim();
    return result;
  }, {});

  const key = prop => `UpdateNames-directDeposit-${prop}`;

  const { register, errors, handleSubmit } = useForm({
    defaultValues,
  });
  const inputValidationRules = {
    maxLength: { value: 30, message: 'Maximum 30 characters' },
    required: 'Missing bank name',
    pattern: {
      value: /^[a-zA-Z0-9 ]*$/,
      message: 'Bank name can only be alphanumeric characters',
    },
  };

  const onSubmit = data => {
    setSaving(true);
    accounts.forEach(account => {
      // MYCC-1710 For new accounts, they are missing the ID data,
      // so we switched to using RN-AN to generate a temporary one
      const temporaryId = `${account.routingNumber}-${account.bankAccountNumber}`;
      const idxToEdit = form.findIndex(
        a => `${a.routingNumber}-${a.bankAccountNumber}` === temporaryId,
      );
      const newBankName = data[`bankName${temporaryId}`].trim();
      form[idxToEdit].bankName = newBankName;
    });
    setForm([...form]);
    setShowDialog(false);
    setConfirm(true);
  };

  const displayAccount = (account, id) => (
    <Table>
      <Table.Head>
        <Table.Row>
          {['Account Type', 'Routing Number', 'Account Number'].map(header => (
            <Table.Cell key={key(`-table-header-${header}-${id}`)}>
              {header}
            </Table.Cell>
          ))}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        <Table.Row key={key(`account-row-${id}`)}>
          <Table.Cell>
            {EnumHelper.valueToOptionLabel(
              mappings.bankAccountTypes,
              account.bankAccountType,
            ).replace(' Account', '')}
          </Table.Cell>
          <Table.Cell>{account.routingNumber}</Table.Cell>
          <Table.Cell>{account.bankAccountNumber}</Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
  );

  return (
    <Modal
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
      open={showDialog}
      scroll="body"
      onClose={() => setShowDialog(false)}
      id="UpdateNamesModal"
      PaperProps={{ style: { maxWidth: 550 } }}
    >
      <Modal.Title onClose={() => setShowDialog(false)}>Bank Name</Modal.Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <Modal.ContentText>
            Provide a bank name for your direct deposit accounts listed below so
            you can easily identify them in the future.
          </Modal.ContentText>
          <Grid container spacing={2}>
            {accounts.map((account, idx) => {
              const accountId = `${account.routingNumber}-${account.bankAccountNumber}`;
              return (
                <Grid item xs={12} key={`account-display-${accountId}`}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} style={{ overflowX: 'auto' }}>
                      {displayAccount(account, accountId)}
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name={`bankName${accountId}`}
                        id={`bankName${accountId}`}
                        placeholder="Bank Name"
                        label={`${idx + 1}. Name of Bank (Max 30 chars.)`}
                        inputRef={register(inputValidationRules)}
                        error={!!errors[`bankName${accountId}`]}
                        helperText={
                          errors[`bankName${accountId}`] &&
                          errors[`bankName${accountId}`].message
                        }
                        inputProps={{
                          maxLength: 30,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Grid container spacing={2} justify="flex-end">
            <Grid item>
              <Button
                secondary
                disabled={saving}
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                loading={saving}
                type="submit"
                id="UpdateNames-SubmitButton"
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Modal.Actions>
      </form>
    </Modal>
  );
};

export default UpdateNamesModal;
